@if (dialogService.isOpen()) {
  <!-- Desktop-only: on mobile the panel already covers the full viewport (inset-0),
       so a scrim behind it would be entirely hidden anyway. Sits between the shell
       header (z-50) and the panel (z-[60]). -->
  <div class="hidden sm:block fixed inset-0 z-[55] bg-black/20" aria-hidden="true" (click)="close()"></div>

  <div
    [class]="'dialog-panel fixed z-[60] bg-base-200 shadow-xl flex flex-col inset-0 sm:inset-auto sm:bottom-24 sm:right-6 sm:rounded-2xl sm:border sm:border-base-300 sm:max-h-[90vh] text-sm '
             + (dialogDebug.active() ? 'sm:w-[520px] sm:h-[800px]' : 'sm:w-[494px] sm:h-[728px]')"
    role="dialog"
    aria-labelledby="dialog-panel-title"
    (keydown.escape)="onEscape()"
  >
    <!-- Header -->
    <div class="flex items-center justify-between gap-2 px-4 py-3 border-b border-base-300 shrink-0">
      <h2 id="dialog-panel-title" class="font-semibold text-base truncate min-w-0">Assistent</h2>
      <div class="flex items-center gap-2 shrink-0">
        <button
          type="button"
          class="btn btn-ghost btn-square h-8 w-8 p-0"
          aria-label="Neue Unterhaltung starten"
          title="Verlauf löschen"
          [disabled]="dialogService.messages().length === 0 || dialogService.status() === 'sending'"
          (click)="resetConversation()"
        >
          <fa-icon [icon]="faTrash"></fa-icon>
        </button>
        <button
          type="button"
          class="btn btn-ghost btn-square h-8 w-8 p-0"
          aria-label="Schliessen"
          (click)="close()"
        >
          <fa-icon [icon]="faXmark"></fa-icon>
        </button>
      </div>
    </div>

    <!-- Debug section: admin-only. The view-context toggle used to live in the header,
         but it's only ever meaningful alongside the rest of this debug data (it controls
         exactly the `view_context` payload shown below), so it lives here now instead of
         being a header control every user sees. -->
    @if (dialogDebug.isAdmin()) {
      <div class="border-b border-base-300 bg-base-200/50 shrink-0">
        <div class="px-3 py-1.5 flex items-center justify-between gap-2">
          <ui-toggle
            [checked]="dialogDebug.enabled()"
            (checkedChange)="toggleDebug($event)"
            color="warning"
            label="Debug"
          ></ui-toggle>
          @if (dialogDebug.active()) {
            <ui-toggle
              [checked]="viewContextService.enabled()"
              (checkedChange)="toggleViewContext($event)"
              color="primary"
              label="Kontext"
            ></ui-toggle>
          }
        </div>

        @if (dialogDebug.active()) {
          <div class="px-3 pb-2 border-t border-base-300/60 pt-2">
            @if (contextLabel(); as label) {
              <p class="text-[11px] text-base-content/60 truncate mb-2">Kontext: {{ label }}</p>
            }

            <div class="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-[11px] font-mono text-base-content/70">
              <span class="text-base-content/40">session</span>
              <span class="break-all">{{ dialogService.sessionId() }}</span>
              <span class="text-base-content/40">household</span>
              <span>{{ contextService.context()?.household?.id ?? '–' }}</span>
              <span class="text-base-content/40">status</span>
              <span>{{ dialogService.status() }}</span>
              <span class="text-base-content/40">context</span>
              @if (dialogService.context()) {
                <button type="button" class="link link-hover text-left w-fit" (click)="openContextModal()">
                  Kontext ansehen
                </button>
              } @else {
                <span>–</span>
              }
            </div>
          </div>
        }
      </div>
    }

    <!-- Scoped to the assistant panel itself, not the browser window: a native
         <dialog>/showModal() centers over the whole viewport regardless of where
         this (small, corner-anchored) panel sits, which reads as a disconnected
         page-level popup. An absolutely-positioned overlay is contained by the
         panel's own box instead (the panel is already `fixed`, which establishes
         the containing block `inset-0` resolves against). -->
    @if (contextModalOpen()) {
      <div
        class="absolute inset-0 z-10 bg-base-100 flex flex-col rounded-none sm:rounded-2xl"
        role="dialog"
        aria-labelledby="context-modal-title"
      >
        <div class="flex items-center justify-between gap-2 px-4 py-3 border-b border-base-300 shrink-0">
          <h3 id="context-modal-title" class="font-semibold text-base">Dialog-Kontext</h3>
          <button type="button" class="btn btn-ghost btn-square h-8 w-8 p-0" aria-label="Schliessen" (click)="closeContextModal()">
            <fa-icon [icon]="faXmark"></fa-icon>
          </button>
        </div>
        <div class="flex-1 overflow-y-auto px-4 py-3">
          @if (decodedContext(); as decoded) {
            <div class="space-y-3">
              <div>
                <p class="text-xs font-semibold uppercase tracking-widest text-base-content/40 mb-1">Payload</p>
                <pre class="text-[11px] font-mono bg-base-200 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">{{ decoded.payloadRaw }}</pre>
              </div>
              <div>
                <p class="text-xs font-semibold uppercase tracking-widest text-base-content/40 mb-1">Signatur</p>
                <p class="text-[11px] font-mono bg-base-200 rounded-lg p-3 break-all">{{ decoded.signature }}</p>
              </div>
            </div>
          } @else {
            <p class="text-sm text-base-content/60 mb-2">Konnte nicht als signierter Kontext dekodiert werden (z.B. ein Mock-Kontext in der lokalen Entwicklung).</p>
            <p class="text-[11px] font-mono bg-base-200 rounded-lg p-3 break-all">{{ dialogService.context() }}</p>
          }
        </div>
      </div>
    }

    <!-- Turn detail overlay: same scoped-to-panel pattern as the context overlay
         above, opened per-message from a click on its bubble (see chat-message's
         `turnDetailRequested`). Shows the exact wire request/response captured on
         that message's `debug` while sending the turn. -->
    @if (turnDetailMessage()) {
      <div
        class="absolute inset-0 z-10 bg-base-100 flex flex-col rounded-none sm:rounded-2xl"
        role="dialog"
        aria-labelledby="turn-detail-modal-title"
      >
        <div class="flex items-center justify-between gap-2 px-4 py-3 border-b border-base-300 shrink-0">
          <h3 id="turn-detail-modal-title" class="font-semibold text-base">Turn-Details</h3>
          <button type="button" class="btn btn-ghost btn-square h-8 w-8 p-0" aria-label="Schliessen" (click)="closeTurnDetail()">
            <fa-icon [icon]="faXmark"></fa-icon>
          </button>
        </div>
        <div class="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          <div>
            <p class="text-xs font-semibold uppercase tracking-widest text-base-content/40 mb-1">Request</p>
            <ui-code-editor [value]="turnDetailRequestJson() ?? ''" lang="json" [readonly]="true" minHeight="120px"></ui-code-editor>
          </div>
          <div>
            <p class="text-xs font-semibold uppercase tracking-widest text-base-content/40 mb-1">Response</p>
            <ui-code-editor [value]="turnDetailResponseJson() ?? ''" lang="json" [readonly]="true" minHeight="120px"></ui-code-editor>
          </div>
        </div>
      </div>
    }

    <!-- Cross-device rejoin banner: shown only when a peek discovered a live session
         under a different session_id while this device already has messages of its
         own — nothing to reconcile otherwise, see DialogService.handlePeekResult. -->
    @if (dialogService.rejoinPrompt(); as rejoin) {
      <div class="px-3 py-2 border-b border-base-300 bg-base-100 flex items-start gap-3 shrink-0" role="alert">
        <div class="flex-1 min-w-0 text-sm">
          <p>Auf einem anderen Gerät läuft noch eine Unterhaltung{{ rejoin.message ? ': "' + rejoin.message + '"' : '' }}.</p>
          <p class="text-xs text-base-content/60 mt-0.5">Möchtest du sie hier fortsetzen?</p>
        </div>
        <div class="flex flex-col gap-1 shrink-0">
          <button type="button" class="btn btn-primary btn-xs" (click)="acceptRejoin()">Fortsetzen</button>
          <button type="button" class="btn btn-ghost btn-xs" (click)="dismissRejoin()">Verwerfen</button>
        </div>
      </div>
    }

    <!-- Messages -->
    <div #messageList class="flex-1 overflow-y-auto px-3 py-3 space-y-2" aria-live="polite" role="log">
      @if (dialogService.messages().length === 0) {
        <div class="text-sm text-base-content/60 px-2 py-4 space-y-2">
          <p>Frag mich zum Beispiel:</p>
          <ul class="space-y-1">
            @for (phrase of examplePhrases; track phrase) {
              <li>
                <button type="button" class="link link-hover text-left" (click)="useExample(phrase)">
                  "{{ phrase }}"
                </button>
              </li>
            }
          </ul>
        </div>
      }
      @for (message of dialogService.messages(); track message.id) {
        @if (dialogDebug.active() && contextDividers().get(message.id); as divider) {
          <div class="flex items-center gap-2 py-1" role="separator" [attr.aria-label]="divider">
            <div class="flex-1 h-px bg-base-300"></div>
            <span class="text-[10px] font-mono text-base-content/40 shrink-0">{{ divider }}</span>
            <div class="flex-1 h-px bg-base-300"></div>
          </div>
        }
        <app-chat-message
          [message]="message"
          [debugActive]="dialogDebug.active()"
          (optionSelected)="onOptionSelected($event)"
          (confirmAnswered)="onConfirmAnswered($event)"
          (retry)="onRetry()"
          (turnDetailRequested)="openTurnDetail($event)"
          (saveRecipeJob)="onSaveRecipeJob($event)"
          (authorSuggestion)="onAuthorSuggestion($event)"
        ></app-chat-message>
      }
    </div>

    <!-- Input -->
    <div class="border-t border-base-300 p-2 flex items-end gap-2 shrink-0">
      @if (dialogService.status() === 'awaiting_input') {
        <button
          type="button"
          class="btn btn-ghost btn-square h-10 w-10 p-0 shrink-0"
          aria-label="Abbrechen"
          title="Abbrechen"
          (click)="onCancel()"
        >
          <fa-icon [icon]="faBan"></fa-icon>
        </button>
      }
      <textarea
        #inputEl
        class="textarea textarea-bordered flex-1 resize-none min-h-10 max-h-32"
        rows="1"
        placeholder="Nachricht eingeben…"
        [(ngModel)]="draft"
        [disabled]="dialogService.status() === 'sending'"
        (keydown)="onKeydown($event)"
      ></textarea>
      <button
        type="button"
        class="btn btn-primary btn-square h-10 w-10 p-0"
        aria-label="Senden"
        [disabled]="dialogService.status() === 'sending' || !draft.trim()"
        (click)="submit()"
      >
        <fa-icon [icon]="faPaperPlane"></fa-icon>
      </button>
    </div>
  </div>
}
